/**
 * Monopage à deux états — rendue par `App.jsx` **avant** `AppShell` quand `status` vaut
 * `activating` ou `suspended` (§ 3.5.3 de la spec CIAM, lot 2) : aucune navigation
 * applicative ne doit être possible tant que le compte n'a pas le rôle `exiplan.utilisateur`.
 *
 * `activating` : Graph n'a pas encore répondu (ou l'attribution est désactivée hors
 * production, § 3.2) — le compte existe, « Réessayer » rejoue `GET /auth/session`
 * (`retry()`), qui retente un `grant` si la date « pas avant » `appRoleAttemptedAt` est échue. Depuis
 * l'onboarding fluide, l'écran se rétablit seul : `SessionContext` re-sonde `/auth/session`
 * (3 s, 5 s, 10 s…, 2 min au plus) et la page n'en montre qu'une indication discrète. La
 * phase `propagating` (rôle attribué, pas encore dans le jeton) change le texte du corps,
 * jamais le statut : un compte tout juste activé n'est jamais « suspendu ».
 *
 * `suspended` : le rôle a été retiré depuis le tenant (§ 3.5.6, kill switch) — le serveur ne
 * le redonne jamais lui-même ; seule une déconnexion est proposée.
 *
 * Aucun détail technique dans les deux cas (pas de code d'erreur Graph, pas de statut HTTP) —
 * textes de copywriter, clés `activation.*`.
 */

import { useTranslation } from 'react-i18next';
import { useSession } from '../contexts/SessionContext.jsx';
import MenuLangue from '../components/common/MenuLangue.jsx';
import { PrimaryButton } from '../components/common.jsx';
import { BRAND, BRAND_GLYPH, BTN_GHOST, EYEBROW } from '../ui/theme.js';
import GlypheExiPlan from '../components/common/GlypheExiPlan.jsx';

/** Indication discrète de l'état du re-sondage automatique (aucun détail technique). */
function indicationSondage({ polling, nextInMs }, t) {
  if (!polling) return t('activation.autoRetryStopped');
  if (nextInMs == null) return t('activation.checking');
  return t('activation.autoRetryIn', { seconds: Math.ceil(nextInMs / 1000) });
}

export function ActivationPage() {
  const { t } = useTranslation();
  const { status, retry, logout, activation } = useSession();
  const suspended = status === 'suspended';
  const propagating = activation?.phase === 'propagating';

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <header className="flex h-11 items-center justify-between border-b-2 border-t-2 border-line px-3">
        <span className={BRAND}>
          <span className={BRAND_GLYPH} aria-hidden="true">
            <GlypheExiPlan />
          </span>
          exiPlan
        </span>
        <MenuLangue />
      </header>

      <main className="flex flex-1 items-center justify-center p-6">
        <div className="w-[440px] max-w-full p-3">
          <div className="relative border-2 border-line bg-surface-2 px-8 py-9 text-center shadow-plank">
            <span className="ticks" aria-hidden="true" />

            <p className={EYEBROW}>{t('activation.eyebrow')}</p>

            {suspended ? (
              <>
                <h1 className="mt-3 text-lg font-semibold text-ink">
                  {t('activation.suspendedTitle')}
                </h1>
                <p role="status" className="mt-3 text-sm text-ink-soft">
                  {t('activation.suspendedBody')}
                </p>
              </>
            ) : (
              <>
                <h1 className="mt-3 text-lg font-semibold text-ink">
                  {t('activation.activatingTitle')}
                </h1>
                <p role="status" className="mt-3 text-sm text-ink-soft">
                  {propagating ? t('activation.propagatingBody') : t('activation.activatingBody')}
                </p>
                {activation && (
                  <p role="status" className="mt-2 text-xs text-ink-soft">
                    {indicationSondage(activation, t)}
                  </p>
                )}
              </>
            )}

            <div className="mt-6 flex flex-col items-center gap-2">
              {!suspended && (
                // Écran fermé, sans en-tête de page : son action unique reste LE primaire.
                <PrimaryButton className="w-full" onClick={retry}>
                  {t('activation.retry')}
                </PrimaryButton>
              )}
              <button type="button" className={`${BTN_GHOST} w-full`} onClick={logout}>
                {t('activation.logout')}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
